<script>
import { mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'CreateModal',
  components: { Multiselect },
  props: {
    editMode: {
      type: Boolean,
      default: false
    },
    branch: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        branch_name: '',
        phone_number: '',
        address: '',
        latitude: '',
        longitude: '',
        check_in_time: '09:00:00',
        check_out_time: '18:00:00',
        operating_days: [],
      },
      days: [
        { label: 'Mon', value: 'Mon' },
        { label: 'Tue', value: 'Tue' },
        { label: 'Wed', value: 'Wed' },
        { label: 'Thu', value: 'Thu' },
        { label: 'Fri', value: 'Fri' },
        { label: 'Sat', value: 'Sat' },
        { label: 'Sun', value: 'Sun' },
      ],
      errors: {},
      isSubmitting: false,
      submitError: '',
    };
  },
  watch: {
    branch: {
      handler(newVal) {
        if (this.editMode && newVal) {
          this.form.branch_name = newVal.branch_name || '';
          this.form.phone_number = newVal.phone_number || '';
          this.form.address = newVal.address || '';
          this.form.latitude = newVal.latitude || '';
          this.form.longitude = newVal.longitude || '';
          this.form.check_in_time = newVal.check_in_time ? newVal.check_in_time.substring(0,5) : '09:00';
          this.form.check_out_time = newVal.check_out_time ? newVal.check_out_time.substring(0,5) : '18:00';
          this.form.operating_days = newVal.operating_days ? newVal.operating_days.split(',').map(day => this.days.find(d => d.value === day)).filter(Boolean) : [];
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('rentalBranch', ['createRentalBranch', 'updateRentalBranch']),
    validate() {
      this.errors = {};
      if (!this.form.branch_name) this.errors.branch_name = 'Branch name is required.';
      if (!this.form.phone_number) this.errors.phone_number = 'Phone number is required.';
      if (!this.form.address) this.errors.address = 'Address is required.';
      // if (!this.form.latitude || isNaN(Number(this.form.latitude))) this.errors.latitude = 'Latitude must be a number.';
      // if (!this.form.longitude || isNaN(Number(this.form.longitude))) this.errors.longitude = 'Longitude must be a number.';
      if (!this.form.check_in_time) this.errors.check_in_time = 'Check-in time is required.';
      if (!this.form.check_out_time) this.errors.check_out_time = 'Check-out time is required.';
      if (!this.form.operating_days.length) this.errors.operating_days = 'Select at least one operating day.';
      return Object.keys(this.errors).length === 0;
    },
    async handleSubmit(e) {
      e.preventDefault();
      if (!this.validate()) return;
      useAlert('Submitting...');
      this.isSubmitting = true;
      this.submitError = '';
      try {
        const payload = {
          branch_name: this.form.branch_name,
          phone_number: this.form.phone_number,
          address: this.form.address,
          // latitude: Number(this.form.latitude),
          // longitude: Number(this.form.longitude),
          latitude: 0,
          longitude: 0,
          check_in_time: this.form.check_in_time,
          check_out_time: this.form.check_out_time,
          operating_days: this.form.operating_days.map(d => d.value).join(','),
        };
        if (this.editMode && this.branch) {
          await this.updateRentalBranch({ branch_id: this.branch.branch_id, payload });
          useAlert('Branch updated successfully');
          this.$emit('updated');
        } else {
          await this.createRentalBranch({ payload });
          useAlert('Branch created successfully');
          this.$emit('created');
        }
      } catch (err) {
        this.submitError = err.message || (this.editMode ? 'Failed to update branch.' : 'Failed to create branch.');
        useAlert(this.submitError);
      } finally {
        this.isSubmitting = false;
      }
    },
    handleClose() {
      this.$emit('close');
    }
  }
};
</script>

<template>
  <div class="z-50 fixed inset-0 flex items-center bg-slate-800/40 px-4">
    <div class="max-h-[80vh] overflow-y-auto mx-auto my-2 p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-xl min-w-[350px] w-full max-w-md relative">
      <button @click="handleClose" class="absolute top-2 right-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl">&times;</button>
      <h2 class="text-xl font-semibold mb-4 text-slate-900 dark:text-slate-100">{{ editMode ? 'Edit Rental Branch' : 'Create Rental Branch' }}</h2>
      <form id="branchForm" @submit="handleSubmit" autocomplete="off" class="flex flex-col gap-3">
        <div>
          <label class="block text-sm font-medium mb-1">Branch Name</label>
          <input v-model="form.branch_name" type="text" class="reset-base w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.branch_name}" />
          <span v-if="errors.branch_name" class="text-xs text-red-500">{{ errors.branch_name }}</span>
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Phone Number</label>
          <input v-model="form.phone_number" type="tel" class="w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.phone_number}" />
          <span v-if="errors.phone_number" class="text-xs text-red-500">{{ errors.phone_number }}</span>
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Address</label>
          <input v-model="form.address" type="text" class="reset-base w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.address}" />
          <span v-if="errors.address" class="text-xs text-red-500">{{ errors.address }}</span>
        </div>
        <!-- <div class="flex gap-2">
          <div class="flex-1">
            <label class="block text-sm font-medium mb-1">Latitude</label>
            <input v-model="form.latitude" type="number" step="any" class="w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.latitude}" />
            <span v-if="errors.latitude" class="text-xs text-red-500">{{ errors.latitude }}</span>
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium mb-1">Longitude</label>
            <input v-model="form.longitude" type="number" step="any" class="w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.longitude}" />
            <span v-if="errors.longitude" class="text-xs text-red-500">{{ errors.longitude }}</span>
          </div>
        </div> -->
        <div class="flex gap-2">
          <div class="flex-1">
            <label class="block text-sm font-medium mb-1">Check-in Time</label>
            <input v-model="form.check_in_time" type="time" class="w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.check_in_time}" />
            <span v-if="errors.check_in_time" class="text-xs text-red-500">{{ errors.check_in_time }}</span>
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium mb-1">Check-out Time</label>
            <input v-model="form.check_out_time" type="time" class="w-full px-3 py-2 border rounded focus:outline-none focus:ring" :class="{'border-red-500': errors.check_out_time}" />
            <span v-if="errors.check_out_time" class="text-xs text-red-500">{{ errors.check_out_time }}</span>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Operating Days</label>
          <multiselect
            v-model="form.operating_days"
            :options="days"
            :multiple="true"
            :close-on-select="false"
            placeholder="Select operating days"
            label="label"
            track-by="value"
            class="w-full"
          />
          <span v-if="errors.operating_days" class="text-xs text-red-500">{{ errors.operating_days }}</span>
        </div>
        <div v-if="submitError" class="text-xs text-red-500">{{ submitError }}</div>
        <div class="flex gap-2 mt-2">
          <button type="button" @click="handleClose" class="flex-1 py-2 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600">Cancel</button>
          <button type="submit" :disabled="isSubmitting" class="flex-1 py-2 rounded bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-60">{{ isSubmitting ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Branch' : 'Create Branch') }}</button>
        </div>
      </form>
    </div>
  </div>
</template>